import type {
  AppleAuthPayload,
  AuthValidateOtpPayload,
  HandoffCode,
  JWT,
  UserFormData,
} from '@/core/data/auth/auth.interface';
import type { CodeResponse } from '@/core/data/auth/google/oauth2';
import type { UserRatings } from '@/core/data/rating/rating.interface';
import { AuthEndpoint, UserEndpoint } from '@/core/network/api/constants/api.url.constant';
import type { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export class AuthApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async authWithEmailUsername(value: string): Promise<AxiosResponse<GamResponse<{ email: string }>>> {
    return await this.httpClient.post(AuthEndpoint.OTP_EMAIL_USERNAME, {
      data: { emailOrUsername: value },
    });
  }

  async authValidateOtp(payload: AuthValidateOtpPayload): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.OTP_VALIDATE, {
      data: payload,
    });
  }

  async authWithGoogle(codeResponse: CodeResponse): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.GOOGLE, {
      data: {
        code: codeResponse.code,
      },
      config: {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XmlHttpRequest',
        },
      },
    });
  }

  async authWithApple(payload: AppleAuthPayload): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.APPLE, {
      data: payload,
    });
  }

  async authGetToken(code: string): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.TOKEN, {
      data: {
        code,
      },
    });
  }

  async authCreateHandoff(): Promise<AxiosResponse<GamResponse<HandoffCode>>> {
    return await this.httpClient.post(AuthEndpoint.HANDOFF, {});
  }

  async authRevokeHandoff(code: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(AuthEndpoint.HANDOFF, {
      data: { code },
    });
  }

  async logout(): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.delete(AuthEndpoint.AUTH);
  }

  async submitPersonalInfo(formData: UserFormData): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.PERSONAL_DETAILS, {
      data: formData,
    });
  }

  async submitRatings(formData: UserRatings): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.RATINGS, {
      data: formData,
    });
  }

  async registerUser(formData: UserFormData): Promise<AxiosResponse<GamResponse<JWT>>> {
    return await this.httpClient.post(AuthEndpoint.OTP_REGISTER, {
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async deleteUser(): Promise<AxiosResponse<GamResponse<boolean>>> {
    return await this.httpClient.delete(UserEndpoint.USERS);
  }
}
